import { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellRing, Volume2, VolumeX, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/config/api';

const API_BASE = API_BASE_URL;

// Notification sound URL (using a simple beep sound)
const NOTIFICATION_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3';

export default function OrderNotifications({ token, onNewOrder }) {
  const [notifications, setNotifications] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('backoffice_sound_enabled');
    return saved === null ? true : saved === 'true';
  });
  const [isOpen, setIsOpen] = useState(false);
  const [lastOrderId, setLastOrderId] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const audioRef = useRef(null);
  const pollingRef = useRef(null);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
    audioRef.current.volume = 0.5;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Audio play failed:', err);
      });
    }
  }, [soundEnabled]);

  // Add new notification
  const addNotification = useCallback((order) => {
    const newNotification = {
      id: `notif-${order.id}-${Date.now()}`,
      orderId: order.id,
      customerName: order.customer_name || 'Guest',
      total: order.total,
      itemCount: order.items?.length || 0,
      orderType: order.order_type,
      timestamp: new Date(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 20)); // Keep last 20
    setUnreadCount(prev => prev + 1);
    playNotificationSound();

    // Show toast notification
    toast.success(
      <div className="flex flex-col">
        <span className="font-semibold">New Order #{order.id}</span>
        <span className="text-sm">{order.customer_name} - ${order.total?.toFixed(2)}</span>
      </div>,
      {
        duration: 5000,
        action: {
          label: 'View',
          onClick: () => onNewOrder?.(order)
        }
      }
    );
  }, [playNotificationSound, onNewOrder]);

  // Poll for new orders
  useEffect(() => {
    if (!token) return;

    const checkNewOrders = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/merchant/orders?status=pending`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        if (response.ok) {
          const orders = await response.json();
          
          if (orders.length > 0) {
            // Find new orders (orders we haven't seen)
            const latestOrder = orders[0];
            
            if (lastOrderId === null) {
              // First load - just set the last order ID without notifications
              setLastOrderId(latestOrder.id);
            } else if (latestOrder.id > lastOrderId) {
              // New order detected!
              const newOrders = orders.filter(o => o.id > lastOrderId);
              newOrders.forEach(order => addNotification(order));
              setLastOrderId(latestOrder.id);
            }
          }
        }
      } catch (error) {
        console.error('Failed to check for new orders:', error);
      }
    };

    // Initial check
    checkNewOrders();

    // Poll every 10 seconds
    pollingRef.current = setInterval(checkNewOrders, 10000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [token, lastOrderId, addNotification]);

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('backoffice_sound_enabled', String(newValue));
    
    if (newValue) {
      // Play a test sound when enabling
      playNotificationSound();
      toast.success('Sound notifications enabled');
    } else {
      toast.info('Sound notifications disabled');
    }
  };

  // Mark all as read
  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  // Clear all notifications
  const clearAll = () => {
    setNotifications([]);
    setUnreadCount(0);
  };

  // Format time ago
  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notifications-bell"
        >
          {unreadCount > 0 ? (
            <BellRing className="h-5 w-5 text-orange-500 animate-pulse" />
          ) : (
            <Bell className="h-5 w-5 text-slate-600" />
          )}
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold animate-bounce">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-96">
        <SheetHeader>
          <div className="flex items-center justify-between">
            <SheetTitle>Order Notifications</SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleSound}
                title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
              >
                {soundEnabled ? (
                  <Volume2 className="h-4 w-4 text-green-600" />
                ) : (
                  <VolumeX className="h-4 w-4 text-slate-400" />
                )}
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Controls */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <Button variant="ghost" size="sm" onClick={markAllRead}>
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  Clear all
                </Button>
              )}
            </div>
          </div>

          {/* Sound toggle */}
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              {soundEnabled ? (
                <Volume2 className="h-4 w-4 text-green-600" />
              ) : (
                <VolumeX className="h-4 w-4 text-slate-400" />
              )}
              <span className="text-sm font-medium">Sound Alerts</span>
            </div>
            <Switch checked={soundEnabled} onCheckedChange={toggleSound} />
          </div>

          {/* Notifications list */}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {notifications.length > 0 ? (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-3 rounded-lg border transition-colors cursor-pointer hover:bg-slate-50 ${
                    notif.read ? 'bg-white border-slate-100' : 'bg-blue-50 border-blue-200'
                  }`}
                  onClick={() => {
                    onNewOrder?.({ id: notif.orderId });
                    setNotifications(prev =>
                      prev.map(n => n.id === notif.id ? { ...n, read: true } : n)
                    );
                    setUnreadCount(prev => Math.max(0, prev - (notif.read ? 0 : 1)));
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">Order #{notif.orderId}</span>
                        {!notif.read && (
                          <Badge className="bg-blue-500 text-[10px] px-1">NEW</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{notif.customerName}</p>
                      <p className="text-sm">
                        {notif.itemCount} item{notif.itemCount !== 1 ? 's' : ''} • ${notif.total?.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {notif.orderType}
                      </Badge>
                      <p className="text-xs text-slate-400 mt-1">
                        {formatTimeAgo(notif.timestamp)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Bell className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No notifications yet</p>
                <p className="text-sm">New orders will appear here</p>
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
